import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2, CalendarCheck } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00", "18:00"];
const INITIAL = { name: "", email: "", phone: "", date: "", time: "", message: "" };

export default function Booking() {
  const { t } = useLang();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.time) return;
    setLoading(true);
    try {
      await axios.post(`${API}/appointment`, form);
      toast.success(t.booking.toastOk, { description: t.booking.toastOkDesc });
      setForm(INITIAL);
    } catch (err) {
      toast.error(t.booking.toastErr, { description: t.booking.toastErrDesc });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rendez-vous" className="relative py-24 sm:py-32" data-testid="booking-section">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FAF8F4] via-[#F0ECE4]/60 to-[#FAF8F4]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow" data-testid="booking-eyebrow">
            <CalendarDays size={13} className="mr-2 inline-block -translate-y-[1px]" />
            {t.booking.eyebrow}
          </p>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-[#3B322A] sm:text-4xl" data-testid="booking-title">
            {t.booking.title1}
            <span className="gold-gradient-text italic">{t.booking.titleAccent}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#5F5647]">{t.booking.desc}</p>
        </div>

        <form onSubmit={handleSubmit} className="card-lux mt-12 rounded-md p-7 sm:p-9" data-testid="booking-form">
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.booking.nameLabel}</label>
              <input required minLength={2} className="field-lux" placeholder={t.booking.namePlaceholder}
                value={form.name} onChange={update("name")} data-testid="booking-name-input" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.booking.emailLabel}</label>
              <input required type="email" className="field-lux" placeholder="vous@hotel.com"
                value={form.email} onChange={update("email")} data-testid="booking-email-input" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.booking.phoneLabel}</label>
              <input type="tel" className="field-lux" placeholder="+33 …"
                value={form.phone} onChange={update("phone")} data-testid="booking-phone-input" />
            </div>
          </div>

          <div className="mt-7 grid gap-7 lg:grid-cols-[2fr_3fr]">
            <div>
              <label className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#948678]">
                <CalendarDays size={13} className="text-[#A87C4F]" />
                {t.booking.dateLabel}
              </label>
              <input required type="date" min={minDate} className="field-lux"
                value={form.date} onChange={update("date")} data-testid="booking-date-input" />
            </div>
            <div>
              <label className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#948678]">
                <Clock size={13} className="text-[#A87C4F]" />
                {t.booking.timeLabel}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setForm({ ...form, time: slot })}
                    data-testid={`booking-slot-${slot.replace(":", "")}`}
                    className={`rounded-sm border px-2 py-2.5 font-mono-eyebrow text-xs transition-colors duration-300 ${
                      form.time === slot
                        ? "border-[#A87C4F] bg-[#A87C4F] font-semibold text-white"
                        : "border-[#A87C4F]/30 text-[#5F5647] hover:border-[#A87C4F]/60 hover:text-[#856237]"
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-7">
            <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.booking.messageLabel}</label>
            <textarea rows={3} className="field-lux resize-none" placeholder={t.booking.messagePlaceholder}
              value={form.message} onChange={update("message")} data-testid="booking-message-textarea" />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-5">
            <button
              type="submit"
              disabled={loading || !form.time}
              data-testid="booking-submit-button"
              className="btn-gold inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <CalendarCheck size={16} />}
              {loading ? t.booking.loading : t.booking.submit}
            </button>
            <p className="text-[11px] text-[#948678]">{t.booking.note}</p>
          </div>
        </form>
      </div>
    </section>
  );
}
